import * as React from "react"
import { CheckIcon, XIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { t } from "@/lib/i18n"
import {
  toastInlineUpdateError,
  toastInlineUpdateSuccess,
  type InlineUpdateResult,
} from "@/lib/inline-update"
import { cn } from "@/lib/utils"

type InlineInputProps = Omit<
  React.ComponentProps<typeof Input>,
  "value" | "defaultValue" | "onSubmit"
> & {
  // Persisted value. Must reflect the committed change once onSubmit resolves
  // (Inertia reloads props on success, so this happens automatically).
  value: string
  onSubmit: (value: string) => Promise<InlineUpdateResult | void>
  successMessage?: string
  errorMessage?: string
  "data-testid"?: string
}

function InlineInput({
  value,
  onSubmit,
  successMessage,
  errorMessage,
  className,
  disabled,
  onKeyDown,
  "data-testid": testId,
  ...props
}: InlineInputProps) {
  const [draft, setDraft] = React.useState(value)
  // Tracks a submitted-but-not-yet-reloaded value so the check/cancel buttons
  // hide right after a successful submit even before props refresh.
  const [committedValue, setCommittedValue] = React.useState<string | null>(null)
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    setDraft(value)
    setCommittedValue(null)
  }, [value])

  const persistedValue = committedValue ?? value
  const dirty = draft !== persistedValue

  const cancel = () => setDraft(persistedValue)

  const submit = () => {
    if (!dirty || submitting) return

    setSubmitting(true)

    onSubmit(draft)
      .then((result) => {
        setCommittedValue(draft)
        toastInlineUpdateSuccess(result, successMessage)
      })
      .catch((error: unknown) => {
        toastInlineUpdateError(error, errorMessage)
      })
      .finally(() => setSubmitting(false))
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event)
    if (event.defaultPrevented) return

    if (event.key === "Enter") {
      event.preventDefault()
      submit()
    }

    if (event.key === "Escape") {
      event.preventDefault()
      cancel()
    }
  }

  // Derive button test ids from the input's test id so several InlineInputs
  // can coexist on the same page without ambiguous selectors.
  const submitTestId = testId ? `${testId}-submit` : "inline-input-submit"
  const cancelTestId = testId ? `${testId}-cancel` : "inline-input-cancel"

  return (
    <div data-slot="inline-input" className="flex items-center gap-1">
      <Input
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled || submitting}
        className={cn("flex-1", className)}
        data-testid={testId}
        {...props}
      />
      {dirty && (
        <>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t("ui.react.components.ui.inline_update.save")}
            data-slot="inline-input-submit"
            data-testid={submitTestId}
            disabled={disabled || submitting}
            onClick={submit}
          >
            <CheckIcon />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t("ui.react.components.ui.inline_update.cancel")}
            data-slot="inline-input-cancel"
            data-testid={cancelTestId}
            disabled={disabled || submitting}
            onClick={cancel}
          >
            <XIcon />
          </Button>
        </>
      )}
    </div>
  )
}

export { InlineInput, type InlineInputProps }
